import { useApi } from '@/api';
import { ChartSkeleton } from '@/components';
import { ILKS_PALETTE } from '@/constants';
import {
  Chart,
  CurrencyValue,
  formatNumber,
  formatPercentage
} from '@blockanalitica/ui';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import _ from 'lodash';
import { useMemo } from 'react';

export interface IlkData {
  symbol: string;
  ilk: string;
  drop: string;
  value: string;
}

export default function CapitalAtRiskGraph() {
  const [data, error] = useApi<IlkData[]>({
    url: `/groups/at-risk/`
  });

  const grouped = _.groupBy(data, 'ilk');

  const datasets = useMemo(() => {
    if (!data) return null;

    const series = [];

    Object.entries(grouped).map(([key, rows]) => {
      series.push({
        label: key,
        backgroundColor: ILKS_PALETTE[key],
        borderColor: ILKS_PALETTE[key],
        data: rows.map((row) => ({
          x: row.drop,
          y: row.value
        }))
      });
    });

    return series;
  }, [grouped, data]);

  const xScaleValueFormatter = useMemo(() => {
    return (value: string) => {
      return formatPercentage(Number(value));
    };
  }, []);

  const subValueFormatter = useMemo(() => {
    return function subValueFormatter(value: string) {
      return <>Price Drop: {formatPercentage(Number(value))}</>;
    };
  }, []);

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      return (
        <CurrencyValue
          value={value.toString()}
          options={{
            currency: 'USD'
          }}
        />
      );
    };
  }, []);

  const config: ChartConfig = useMemo(() => {
    return {
      type: 'line',
      options: {
        fill: true,
        elements: {
          line: {
            borderWidth: 2
          }
        },
        scales: {
          x: {
            type: 'linear',
            reverse: true
          },
          y: {
            stacked: true
          }
        }
      }
    };
  }, []);

  if (error) {
    throw error;
  }

  if (datasets) {
    return (
      <Chart
        chartDatasets={datasets}
        config={config}
        defaultDataIndex={5}
        xScaleValueFormatter={xScaleValueFormatter}
        subValueFormatter={subValueFormatter}
        valueFormatter={valueFormatter}
        tooltip={{
          titleFormatter: (_value, tootlipItems) =>
            // @ts-ignore
            `Price Drop: ${formatPercentage(tootlipItems[0].raw.x)}`,
          valueFormatter: (value) => `$${formatNumber(Number(value))}`
        }}
        showKpisAsTooltip
      />
    );
  }
  return <ChartSkeleton />;
}
